// ---------------------------------------------------------------------------
// Geo targeting data layer (OPUS-D09 / feature #72)
// ---------------------------------------------------------------------------
// TanStack Query hooks + lightweight runtime validators for the
// campaign-geo-targeting endpoints. Mirrors the API contract from
// apps/api/src/routes/campaign-geo-targeting.ts (ISO-3166 country/region/city,
// allow/deny mode, per-campaign rule rows).
//
// This module intentionally avoids zod + react-hook-form because the D01
// scaffold locks the dash package.json to TanStack Query + Sonner only (new
// deps are a root-owned bootstrap). Validators here are hand-rolled and
// match the server-side regex suite exactly so rejects surface identically.
// ---------------------------------------------------------------------------
//
// Usage:
//   const { data } = useGeoTargeting(campaignId);
//   const { mutate: save } = useSaveGeoTargeting();
//   save({ campaignId, mode: "allow", rules: [{ country: "US" }] });
// ---------------------------------------------------------------------------

import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";

// ---- domain types (shape-matched to the api router) ----------------------

export const GEO_MODES = ["allow", "deny"] as const;
export type GeoMode = (typeof GEO_MODES)[number];

export interface GeoRule {
	country: string;
	region?: string;
	city?: string;
}

export interface GeoTargeting {
	id: string;
	tenant_id: string;
	campaign_id: string;
	mode: GeoMode;
	rules: GeoRule[];
	created_at: number;
	updated_at: number;
}

export interface SaveGeoInput {
	campaignId: string;
	mode: GeoMode;
	rules: GeoRule[];
}

// ---- campaign-id + iso regexes (must match the server) ------------------

const CAMPAIGN_ID_RE = /^[a-z0-9_-]{1,64}$/i;
const COUNTRY_RE = /^[A-Z]{2}$/;
const REGION_RE = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;
const CITY_MAX_LEN = 80;
export const MAX_RULES = 500;

export function isValidCampaignId(value: string): boolean {
	return CAMPAIGN_ID_RE.test(value);
}

export function normaliseCountry(raw: string): string | null {
	const up = raw.trim().toUpperCase();
	return COUNTRY_RE.test(up) ? up : null;
}

export function normaliseRegion(raw: string): string | null {
	const up = raw.trim().toUpperCase();
	return REGION_RE.test(up) ? up : null;
}

export function normaliseCity(raw: string): string | null {
	// strip ASCII control chars; collapse whitespace
	const cleaned = raw
		.replace(/[\u0000-\u001F\u007F]/g, "")
		.replace(/\s+/g, " ")
		.trim();
	if (cleaned.length === 0 || cleaned.length > CITY_MAX_LEN) return null;
	return cleaned;
}

/**
 * Validate + normalise a draft rule from form input. Returns either the
 * cleaned rule or an error describing the first failure. Rules here must
 * satisfy the same invariants as the server's `normaliseRule`:
 *   - country is mandatory, alpha-2 ISO code
 *   - region, when present, must share the country prefix (`US-*`)
 *   - city is free text up to 80 chars, control chars stripped
 */
export function validateRuleDraft(draft: {
	country: string;
	region?: string;
	city?: string;
}): { ok: true; rule: GeoRule } | { ok: false; error: string } {
	const country = normaliseCountry(draft.country ?? "");
	if (!country) {
		return { ok: false, error: "Country must be a 2-letter ISO code (e.g. US)." };
	}
	const out: GeoRule = { country };

	if (draft.region && draft.region.trim().length > 0) {
		const region = normaliseRegion(draft.region);
		if (!region) {
			return { ok: false, error: "Region must look like US-CA (country-subdivision)." };
		}
		if (!region.startsWith(`${country}-`)) {
			return { ok: false, error: `Region ${region} does not belong to ${country}.` };
		}
		out.region = region;
	}

	if (draft.city && draft.city.trim().length > 0) {
		const city = normaliseCity(draft.city);
		if (!city) {
			return { ok: false, error: "City must be 1-80 printable characters." };
		}
		out.city = city;
	}

	return { ok: true, rule: out };
}

/**
 * Dedup + order a rule list before saving. Matches the server's ordering so
 * the optimistic UI mirrors what the API returns.
 */
export function dedupeRules(rules: GeoRule[]): GeoRule[] {
	const seen = new Set<string>();
	const out: GeoRule[] = [];
	for (const r of rules) {
		const key = `${r.country}|${r.region ?? ""}|${r.city ?? ""}`;
		if (seen.has(key)) continue;
		seen.add(key);
		out.push(r);
	}
	return out;
}

export function ruleLabel(rule: GeoRule): string {
	if (rule.city && rule.region) return `${rule.city}, ${rule.region}`;
	if (rule.city) return `${rule.city}, ${rule.country}`;
	if (rule.region) return rule.region;
	return rule.country;
}

// ---- base url resolution --------------------------------------------------

/**
 * Resolves the api base url for the dashboard. Dash runs on :5173 in local
 * dev and the api on :8787. In prod, dash is served from the same origin.
 */
export function resolveApiBase(): string {
	const envBase =
		typeof import.meta !== "undefined" &&
		(import.meta as unknown as { env?: Record<string, string> }).env?.[
			"VITE_API_BASE"
		];
	if (envBase && envBase.length > 0) return envBase.replace(/\/$/, "");
	if (typeof window !== "undefined") {
		const { hostname, protocol } = window.location;
		if (hostname === "localhost" || hostname === "127.0.0.1") {
			return `${protocol}//${hostname}:8787`;
		}
	}
	return "";
}

function resolveTenantId(): string {
	const envTenant =
		typeof import.meta !== "undefined" &&
		(import.meta as unknown as { env?: Record<string, string> }).env?.[
			"VITE_TENANT_ID"
		];
	return envTenant && envTenant.length > 0 ? envTenant : "demo";
}

// ---- fetchers -------------------------------------------------------------

export class GeoApiError extends Error {
	readonly status: number;
	readonly code: string;
	constructor(status: number, code: string, message?: string) {
		super(message ?? `${code} (${status})`);
		this.name = "GeoApiError";
		this.status = status;
		this.code = code;
	}
}

async function callApi<T>(
	path: string,
	init: RequestInit,
	signal?: AbortSignal,
): Promise<T> {
	const base = resolveApiBase();
	const url = `${base}/api/v1/campaign-geo-targeting${path}`;
	const res = await fetch(url, {
		...init,
		signal,
		headers: {
			Accept: "application/json",
			"Content-Type": "application/json",
			"x-tenant-id": resolveTenantId(),
			...(init.headers ?? {}),
		},
		credentials: "include",
	});
	const contentType = res.headers.get("content-type") ?? "";
	const payload: unknown = contentType.includes("application/json")
		? await res.json().catch(() => null)
		: null;
	if (!res.ok) {
		const code =
			payload &&
			typeof payload === "object" &&
			"error" in payload &&
			typeof (payload as { error?: unknown }).error === "string"
				? (payload as { error: string }).error
				: `http_${res.status}`;
		throw new GeoApiError(res.status, code);
	}
	return payload as T;
}

export async function fetchGeoTargeting(
	campaignId: string,
	signal?: AbortSignal,
): Promise<GeoTargeting | null> {
	if (!isValidCampaignId(campaignId)) return null;
	try {
		const body = await callApi<{ targeting: GeoTargeting }>(
			`/${encodeURIComponent(campaignId)}`,
			{ method: "GET" },
			signal,
		);
		return body.targeting;
	} catch (err) {
		if (err instanceof GeoApiError && err.status === 404) return null;
		throw err;
	}
}

export async function upsertGeoTargeting(
	input: SaveGeoInput,
): Promise<GeoTargeting> {
	const body = await callApi<{ targeting: GeoTargeting }>("/", {
		method: "POST",
		body: JSON.stringify({
			campaignId: input.campaignId,
			mode: input.mode,
			rules: dedupeRules(input.rules),
		}),
	});
	return body.targeting;
}

// ---- react-query hooks ----------------------------------------------------

export const GEO_TARGETING_QK = ["geo-targeting"] as const;

export function geoTargetingQueryKey(campaignId: string) {
	return [...GEO_TARGETING_QK, campaignId] as const;
}

export function useGeoTargeting(
	campaignId: string,
): UseQueryResult<GeoTargeting | null, Error> {
	const valid = campaignId.length > 0 && isValidCampaignId(campaignId);
	return useQuery<GeoTargeting | null, Error>({
		queryKey: geoTargetingQueryKey(campaignId),
		queryFn: ({ signal }) => fetchGeoTargeting(campaignId, signal),
		enabled: valid,
		staleTime: 15_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
}

export function useSaveGeoTargeting(): UseMutationResult<
	GeoTargeting,
	Error,
	SaveGeoInput
> {
	const qc = useQueryClient();
	return useMutation<GeoTargeting, Error, SaveGeoInput>({
		mutationFn: upsertGeoTargeting,
		onSuccess: (data) => {
			qc.setQueryData(geoTargetingQueryKey(data.campaign_id), data);
			void qc.invalidateQueries({ queryKey: geoTargetingQueryKey(data.campaign_id) });
		},
	});
}
